import { useRef, useState, useCallback } from 'react';
import { useMediaQuery } from './use-mobile';

/** Powiększenie warstwy lupy — 2x, w zalecanym przedziale 1.8–2.2x. */
export const MAGNIFIER_ZOOM = 2;

/**
 * Efekt lupy dla zdjęć produktów: po najechaniu kursorem pokazuje powiększony
 * fragment zdjęcia WEWNĄTRZ tego samego, niezmienionego boxa (bez zmiany
 * layoutu, bez wychodzenia poza granice kontenera) — pozycja liczona z
 * clientX/clientY kursora względem kontenera, punkt pod kursorem zostaje
 * w centrum powiększenia.
 *
 * Pozycja warstwy lupy jest aktualizowana bezpośrednio przez DOM (ref.style),
 * NIE przez React state — dzięki temu ruch kursora nie wywołuje re-renderu
 * komponentu. `isZooming` (jedyny stan) zmienia się tylko przy wejściu/wyjściu.
 *
 * Aktywne wyłącznie na urządzeniach z hover + fine pointer (mysz/trackpad) —
 * na dotyku nigdy się nie włącza, dotyk zachowuje swój dotychczasowy
 * mechanizm (nawigacja / galeria).
 */
export function useImageMagnifier() {
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomImgRef    = useRef<HTMLImageElement>(null);
  const [isZooming, setIsZooming] = useState(false);
  const enabled = useMediaQuery('(hover: hover) and (pointer: fine)');

  const updatePosition = useCallback((clientX: number, clientY: number) => {
    const container = containerRef.current;
    const zoomImg    = zoomImgRef.current;
    if (!container || !zoomImg) return;
    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const fx = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const fy = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
    const left = -fx * (MAGNIFIER_ZOOM - 1) * rect.width;
    const top  = -fy * (MAGNIFIER_ZOOM - 1) * rect.height;
    zoomImg.style.transform = `translate(${left}px, ${top}px) scale(${MAGNIFIER_ZOOM})`;
  }, []);

  const onMouseEnter = useCallback((e: React.MouseEvent) => {
    if (!enabled) return;
    updatePosition(e.clientX, e.clientY);
    setIsZooming(true);
  }, [enabled, updatePosition]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!enabled) return;
    updatePosition(e.clientX, e.clientY);
  }, [enabled, updatePosition]);

  const onMouseLeave = useCallback(() => {
    if (!enabled) return;
    setIsZooming(false);
  }, [enabled]);

  return { containerRef, zoomImgRef, isZooming, enabled, onMouseEnter, onMouseMove, onMouseLeave };
}
